import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { apiLogin, apiMe, clearToken } from '@/lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth muss innerhalb von AuthProvider verwendet werden');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSupabaseConfigured()) {
      // API-Auth (Express-Backend mit JWT). Session aus gespeichertem Token wiederherstellen.
      apiMe().then(me => {
        if (me) {
          setUser({ id: me.id, email: me.email });
          setProfile({ id: me.id, full_name: me.full_name, role: me.role, email: me.email, phone: me.phone });
        }
        setLoading(false);
      });
      return;
    }

    // Aktuelle Session laden
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) loadProfile(session.user.id);
      setLoading(false);
    });

    // Auth-Änderungen beobachten
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id);
      } else {
        setProfile(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadProfile = async (authId) => {
    if (!isSupabaseConfigured()) return;
    try {
      const { data } = await supabase
        .from('users')
        .select('*')
        .eq('auth_id', authId)
        .single();
      if (data) setProfile(data);
    } catch (error) {
      console.error('Profil laden fehlgeschlagen:', error);
    }
  };

  const signIn = async (email, password) => {
    if (!isSupabaseConfigured()) {
      const res = await apiLogin(email, password);
      if (!res.ok) return { error: { message: res.error } };
      setUser({ id: res.user.id, email: res.user.email });
      setProfile({ id: res.user.id, full_name: res.user.full_name, role: res.user.role, email: res.user.email, phone: res.user.phone });
      return { error: null };
    }
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { data, error };
  };

  const signOut = async () => {
    if (!isSupabaseConfigured()) {
      clearToken();
      setUser(null);
      setProfile(null);
      return;
    }
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  };

  const value = {
    user,
    profile,
    loading,
    signIn,
    signOut,
    isAdmin: profile?.role === 'admin',
    isManager: profile?.role === 'manager' || profile?.role === 'admin',
    isAuthenticated: !!user,
    isDemoMode: !isSupabaseConfigured()
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
